import "amazon-connect-streams";
import { useEffect, useCallback, useState } from "react";
import OverLayCcp from "./OverlayCcp";
import CallerIdOverlayCcp from "./CallerIdOverlayCcp";
import { useAppDispatch, useAppSelector } from "../../../store/types";
import { getCcpInitializedStatus, getCcpAgent } from "../../../services/ccp";
import { getFeatureFlags } from "../../../services/featureFlags";
import { getCallHistory } from "../../../services/history";
import {
  getSettings,
  createOrUpdateLocaleSettings,
  getCCPLocaleStatus,
} from "../../../services/settings";
import { getGlobalConfig } from "../../../services/globalConfig";
import languageConfig from "../../../components/SbuxSettings/config/language.json";
import { getMetricsQueue, getMyMetricsData } from "../../../services/metrics";
import { getResources } from "../../../services/resources";
import { getTabsStatus } from "../../../services/tabs";
import { setOutboundCallerId } from "../../../services/outboundCall";
import { clientLog } from "../../../services/logging";
import { logMessage } from "../../../utils/amplifyLogger";
import styles from "../styles";
import useCss from "../../../hooks/useCss";
import {
  getCcpQueuesSelector,
  getCcpAgentRoutingNameSelector,
} from "../../../selectors/ccpSelector";
import {
  getConnectedInstanceSelector,
  getIsOutboundPhoneEnabledSelector,
  getOutboundPhonesSelector,
} from "../../../selectors/userInstanceSelector";
import {
  getLocaleSelector,
  getCcpLocaleStatusSelector,
  getTranslatedStatusSelector,
} from "../../../selectors/settingsSelector";

interface InitCcpProps {
  ccpUrl: string;
  loginUrl: string;
}

const InitCcp = ({ ccpUrl, loginUrl }: InitCcpProps) => {
  const dispatch = useAppDispatch();
  const classes = useCss(styles);

  const queues = useAppSelector(getCcpQueuesSelector);
  const agentRoutingName = useAppSelector(getCcpAgentRoutingNameSelector);
  const connectedInstance = useAppSelector(getConnectedInstanceSelector);
  const isOutboundPhoneEnabled = useAppSelector(
    getIsOutboundPhoneEnabledSelector,
  );
  const outboundPhones = useAppSelector(getOutboundPhonesSelector);
  const locale = useAppSelector(getLocaleSelector);
  const ccpLocaleStatus = useAppSelector(getCcpLocaleStatusSelector);
  const translatedStatus = useAppSelector(getTranslatedStatusSelector);

  const instanceId = connectedInstance ? connectedInstance.instanceId : null;
  const instanceArn = connectedInstance ? connectedInstance.arn : null;
  const [isIframeRendered, setIsIframeRendered] = useState(false);

  const metricsPayload = {
    instanceId,
    queues,
  };
  const myMetricsPayload = {
    instanceArn,
  };

  const handleCcpInitialize = useCallback(async () => {
    await dispatch(getCcpInitializedStatus(true));
  }, []);

  const handleAgentInitialize = useCallback(async (agent: connect.Agent) => {
    agent.getEndpoints(agent.getAllQueueARNs(), {
      success: async (data: { endpoints: connect.Endpoint[] }) => {
        await dispatch(
          getCcpAgent({
            agentStatus: agent.getStatus().name,
            configuration: agent.getConfiguration(),
            endpoints: data.endpoints,
          }),
        );
      },
      failure: (err: string) => {
        logMessage(`Init CCP`, `Failed. ${err}`, `error`);
      },
    });
  }, []);

  const handleIframOnLoad = useCallback(() => setIsIframeRendered(true), []);

  useEffect(() => {
    try {
      if (instanceId) {
        // Only load CCP/core data, not manager tab data
        (async () => {
          const typedOutboundPhones = outboundPhones as
            | Array<{ value: string }>
            | undefined;
          if (typedOutboundPhones && typedOutboundPhones.length > 0) {
            await dispatch(setOutboundCallerId(typedOutboundPhones[0].value));
          }
          await dispatch(getSettings());
          await dispatch(getGlobalConfig());
          await dispatch(
            createOrUpdateLocaleSettings(languageConfig.translations),
          );
          await dispatch(getFeatureFlags());
          await dispatch(getCallHistory());
          // Removed: getClosureMsg, getTemporaryMsg, getMainHours, getMediaMgt, getFeatureFlags
        })();
      } else {
        dispatch(getTabsStatus("disabled"));
      }
    } catch (error) {
      dispatch(getTabsStatus("disabled"));
      clientLog({
        component: `initCCP`,
        message: `Something went wrong in calling all API => ${error}`,
      });
    }
  }, [instanceId, outboundPhones]);

  useEffect(() => {
    if (isIframeRendered) {
      const container = document.getElementById("ccp");
      window.connect?.core?.initCCP(container, {
        ccpUrl: ccpUrl,
        loginPopup: false,
        softphone: {
          allowFramedSoftphone: true,
        },
        loginOptions: {
          height: 400,
        },
        pageOptions: {
          enablePhoneTypeSettings: false,
        },
      });

      if (window.connect?.core) {
        window.connect.core.onInitialized(handleCcpInitialize);
      }

      if (window.connect?.agent) {
        window.connect.agent(handleAgentInitialize);
      } else {
        /* eslint-disable no-console */
        console.error("Amazon Connect not properly initialized");
        /* eslint-enable no-console */
      }
    }
  }, [isIframeRendered, ccpUrl, handleCcpInitialize, handleAgentInitialize]);

  useEffect(() => {
    if (!!agentRoutingName && instanceId) {
      Promise.allSettled([
        dispatch(getMetricsQueue(metricsPayload)),
        dispatch(getMyMetricsData(myMetricsPayload)),
        dispatch(getResources()),
      ])
        .then(() => dispatch(getTabsStatus("enabled")))
        .catch((error) => {
          dispatch(getTabsStatus("disabled"));
          clientLog({
            component: `initCCP`,
            message: `Something went wrong in calling all API => ${error}`,
          });
        });
    }
  }, [agentRoutingName, instanceId, metricsPayload, myMetricsPayload]);

  useEffect(() => {
    if (ccpLocaleStatus === "updated" && translatedStatus === "success") {
      (async () => {
        await window.connect.agent((agent: connect.Agent) => {
          const config = agent.getConfiguration();
          const language = (
            languageConfig.lanaguages as Array<{
              code: string;
              ccpCode?: string;
            }>
          ).find((f) => f.code === locale);
          const userPreferedLocale = language?.ccpCode || null;
          if (userPreferedLocale && config.agentPreferences) {
            (config.agentPreferences as { locale?: string }).locale =
              userPreferedLocale;
            /* eslint-disable no-console */
            agent.setConfiguration(config, {
              success: () => console.log("Success setting config"),
              failure: (err: string) =>
                console.warn("Failed to set config", err),
            });
            /* eslint-enable no-console */
          }
        });
        await dispatch(getCCPLocaleStatus());
      })();
    }
  }, [ccpLocaleStatus, translatedStatus, locale]);

  return (
    <>
      {!isIframeRendered ? (
        <iframe
          className={classes.iframeDisplay}
          onLoad={handleIframOnLoad}
          src={loginUrl}
          id={"iframe"}
          frameBorder="0"
        />
      ) : (
        isIframeRendered && (
          <div
            id="ccp"
            style={
              isOutboundPhoneEnabled
                ? {
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: "-1px",
                  }
                : {}
            }
            className={classes.ccpRoot}
          >
            <CallerIdOverlayCcp />
            <OverLayCcp />
          </div>
        )
      )}
    </>
  );
};

export default InitCcp;
